import { useState } from 'react'
import { ChartBarIcon, DocumentArrowDownIcon, CalendarIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline'

interface SalesData {
  month: string
  sales: number
  orders: number
  customers: number
}

interface TopProduct {
  name: string
  quantity: number
  revenue: number
}

export default function Reports() {
  const [selectedPeriod, setSelectedPeriod] = useState('last_month')

  const salesData: SalesData[] = [
    { month: 'Enero', sales: 45000, orders: 320, customers: 180 },
    { month: 'Febrero', sales: 52000, orders: 380, customers: 210 },
    { month: 'Marzo', sales: 48000, orders: 340, customers: 195 },
    { month: 'Abril', sales: 56000, orders: 420, customers: 240 },
    { month: 'Mayo', sales: 61000, orders: 450, customers: 265 },
    { month: 'Junio', sales: 58000, orders: 430, customers: 250 }
  ]

  const topProducts: TopProduct[] = [
    { name: 'Pan Artesanal', quantity: 850, revenue: 12750 },
    { name: 'Croissants', quantity: 620, revenue: 9300 },
    { name: 'Magdalenas', quantity: 450, revenue: 6750 },
    { name: 'Pan de Molde', quantity: 380, revenue: 5700 },
    { name: 'Empanadas', quantity: 320, revenue: 4800 }
  ]

  const currentMonth = salesData[salesData.length - 1]
  const previousMonth = salesData[salesData.length - 2]

  const getSalesGrowth = () => {
    const growth = ((currentMonth.sales - previousMonth.sales) / previousMonth.sales) * 100
    return growth.toFixed(1)
  }

  const getOrdersGrowth = () => {
    const growth = ((currentMonth.orders - previousMonth.orders) / previousMonth.orders) * 100
    return growth.toFixed(1)
  }

  return (
    <div>
      <div className="mb-8">
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-gray-900">Reportes y Análisis</h1>
            <p className="mt-1 text-sm text-gray-600">
              Genera reportes y analiza métricas del negocio
            </p>
          </div>
          <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            >
              <option value="last_week">Última Semana</option>
              <option value="last_month">Último Mes</option>
              <option value="last_quarter">Último Trimestre</option>
              <option value="last_year">Último Año</option>
            </select>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <DocumentArrowDownIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
              Exportar PDF
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Ventas del Mes</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      €{currentMonth.sales.toLocaleString()}
                    </div>
                    <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                      parseFloat(getSalesGrowth()) >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {parseFloat(getSalesGrowth()) >= 0 ? '+' : ''}{getSalesGrowth()}%
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-8 w-8 text-blue-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Órdenes</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">{currentMonth.orders}</div>
                    <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                      parseFloat(getOrdersGrowth()) >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {parseFloat(getOrdersGrowth()) >= 0 ? '+' : ''}{getOrdersGrowth()}%
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-bold text-sm">{currentMonth.customers}</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Clientes Únicos</dt>
                  <dd className="text-lg font-medium text-gray-900">Este mes</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalendarIcon className="h-8 w-8 text-orange-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Promedio por Orden</dt>
                  <dd className="text-2xl font-semibold text-gray-900">
                    €{(currentMonth.sales / currentMonth.orders).toFixed(0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Sales Chart */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Evolución de Ventas</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {salesData.map((data) => (
                <div key={data.month} className="flex items-center">
                  <div className="w-16 text-sm text-gray-600">{data.month}</div>
                  <div className="flex-1 mx-4">
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-primary-600 h-3 rounded-full"
                        style={{ width: `${(data.sales / 65000) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="w-20 text-sm font-medium text-gray-900 text-right">
                    €{data.sales.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Products */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Productos Más Vendidos</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {topProducts.map((product, index) => (
                <div key={product.name} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-md flex items-center justify-center">
                      <span className="text-primary-600 font-bold text-sm">{index + 1}</span>
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">{product.name}</div>
                      <div className="text-sm text-gray-500">{product.quantity} unidades</div>
                    </div>
                  </div>
                  <div className="text-sm font-medium text-gray-900">
                    €{product.revenue.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Performance Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Rendimiento Mensual</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ventas
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Órdenes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Clientes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Promedio/Orden
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {salesData.map((data) => (
                <tr key={data.month} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{data.month}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">€{data.sales.toLocaleString()}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{data.orders}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{data.customers}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">€{(data.sales / data.orders).toFixed(0)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}